import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Record } from 'immutable';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Utils
import * as MeetingItemUtils from 'Shared/utils/MeetingItem.utils';

// Actions
import * as meetingItemActions from 'Shared/actions/MeetingItems.actions';

// Components
import { WpCollapsible } from 'Wpds/components/collapsible';
import WpIcon from 'Wpds/components/icon/WpIcon';
import AttachmentsContainer from 'Shared/components/Attachments/AttachmentsContainer';

// Styles
import './MeetingItemDetailPanelAttachments.scss';

type Props = {
  meetingItem: Record<MeetingItem>;
  fileUploaderRef?: { current: HTMLInputElement };
};

const MeetingItemDetailPanelAttachments = ({ meetingItem, fileUploaderRef }: Props) => {
  const dispatch = useDispatch();
  const [showEmpty, setShowEmpty] = useState(false);
  const [isOpen, toggleIsOpen] = useToggle(true);
  const { tr } = useTranslation();

  const uuid = MeetingItemHelpers.getUuid(meetingItem);
  const attachments = MeetingItemHelpers.getAttachments(meetingItem);
  const hasAttachments = MeetingItemUtils.hasAttachments(attachments);

  useEffect(() => {
    if (hasAttachments) setShowEmpty(false);
    else setShowEmpty(true);
  }, [hasAttachments]);

  const handleAttachFileClick = useCallback(() => {
    fileUploaderRef.current.click();
  }, [fileUploaderRef]);

  const handleAttachFile = useCallback(
    file => {
      setShowEmpty(false);
      const data = new FormData();
      data.append('file', file);
      return dispatch(meetingItemActions.attachFile(uuid, data));
    },
    [dispatch, uuid]
  );

  const handleDeleteAttachment = useCallback(
    attachmentId => {
      return dispatch(meetingItemActions.deleteAttachment(uuid, attachmentId));
    },
    [dispatch, uuid]
  );

  const getTitleLabel = () => {
    const countOfItems = attachments.size;
    if (countOfItems === 1) return tr('perform.meeting_item.attachments.count_one');
    if (countOfItems > 1) return tr('perform.meeting_item.attachments.count_many', `${countOfItems}`);
    return tr('perform.meeting_item.attachments.title');
  };

  const baseClassName = 'MeetingItemDetailPanelAttachments';
  return (
    <div>
      <div className={`${baseClassName}__header`}>
        <div className={`${baseClassName}__title-wrapper`} onClick={() => toggleIsOpen()}>
          <p className={`${baseClassName}__title-wrapper__title`}>{getTitleLabel()}</p>
          <WpIcon name={isOpen ? 'angle-down' : 'angle-right'} />
        </div>
      </div>

      <WpCollapsible open={isOpen}>
        {!hasAttachments && showEmpty && (
          <div className={`${baseClassName}__empty`} onClick={handleAttachFileClick}>
            {tr('perform.meeting_item.attachments.upload_attachments')}
          </div>
        )}
        <div className={`${baseClassName}__attachments-list`}>
          <AttachmentsContainer
            attachments={attachments.toJS()}
            fileUploaderRef={fileUploaderRef}
            handleUploadAttachments={handleAttachFile}
            enableFileUploader
            handleDeleteAttachment={handleDeleteAttachment}
            options={{
              showPreview: false,
              view: 'list',
            }}
          />
        </div>
      </WpCollapsible>
    </div>
  );
};

export default MeetingItemDetailPanelAttachments;
